import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Medal, Trophy, FileDown, Save, Users } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Kpi({ label, value, icon: Icon, testId }) {
  return (
    <div className="stat-card" data-testid={testId}>
      <div className="overline mb-1 flex items-center gap-1">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className="stat-number" style={{ fontSize: "2.2rem" }}>{value}</div>
    </div>
  );
}

function ConfigCard({ config, onSaved }) {
  const [form, setForm] = useState(config);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setForm(config), [config]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onSaved(form);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card p-4 flex flex-wrap items-end gap-4" onSubmit={save} data-testid="premiacao-config">
      <div>
        <label className="overline block mb-1">Troféu até</label>
        <select
          data-testid="premiacao-trofeus-ate"
          className="select-field"
          value={form.trofeus_ate}
          onChange={(e) => setForm({ ...form, trofeus_ate: parseInt(e.target.value) })}
        >
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <option key={n} value={n}>{n}º lugar</option>
          ))}
        </select>
      </div>
      <div>
        <label className="overline block mb-1">Medalha até</label>
        <select
          data-testid="premiacao-medalhas-ate"
          className="select-field"
          value={form.medalhas_ate}
          onChange={(e) => setForm({ ...form, medalhas_ate: parseInt(e.target.value) })}
        >
          <option value={form.trofeus_ate}>Sem medalhas</option>
          {[2, 3, 4, 5, 6, 7, 8, 10].filter((n) => n > form.trofeus_ate).map((n) => (
            <option key={n} value={n}>{n}º lugar</option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="premiacao-trofeu-overall"
          checked={!!form.trofeu_overall}
          onChange={(e) => setForm({ ...form, trofeu_overall: e.target.checked })}
        />
        1 troféu por Overall
      </label>
      <button type="submit" className="btn-primary !py-2 !text-xs inline-flex items-center gap-1" disabled={busy} data-testid="premiacao-config-save">
        <Save size={13} /> {busy ? "Salvando..." : "Salvar regra"}
      </button>
      {error && <div className="text-xs text-[#FF6B6B] w-full" data-testid="premiacao-config-error">{error}</div>}
    </form>
  );
}

export default function Premiacao() {
  const { user } = useAuth();
  const { currentId } = useCompetition();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    if (!currentId) return;
    setError("");
    try {
      const { data } = await api.get(`/competitions/${currentId}/premiacao`);
      setData(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [currentId]);

  const saveConfig = async (cfg) => {
    const { data } = await api.put(`/competitions/${currentId}/premiacao-config`, cfg);
    setData(data);
  };

  const t = data?.totals;
  const cfg = data?.config;
  const posLabel = (p) => `${p}º`;

  return (
    <div data-testid="premiacao-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-1"><Medal size={12} /> Mesa de troféus e medalhas</div>
          <h1 className="font-display text-5xl font-black tracking-tight">PREMIAÇÃO</h1>
          <div className="text-xs text-[var(--text-secondary)] mt-2">
            Calculado automaticamente pelos inscritos de cada categoria. Conta todos os inscritos (inclusive W.O.).
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="premiacao-pdf"
            className="btn-primary flex items-center gap-2"
            disabled={!currentId}
            onClick={() => window.open(`${BACKEND}/api/export/premiacao.pdf?competition_id=${currentId}`, "_blank")}
          >
            <FileDown size={16} /> Lista de Premiação (PDF)
          </button>
          <CompetitionSelector />
        </div>
      </div>

      {error && <div className="card p-3 mb-4 text-sm text-[#FF6B6B]" data-testid="premiacao-error">{error}</div>}

      {cfg && isAdmin && (
        <div className="mb-4">
          <ConfigCard config={cfg} onSaved={saveConfig} />
        </div>
      )}
      {cfg && !isAdmin && (
        <div className="text-xs text-[var(--text-secondary)] mb-4" data-testid="premiacao-rule">
          Regra: troféu do 1º ao {cfg.trofeus_ate}º
          {cfg.medalhas_ate > cfg.trofeus_ate ? ` · medalha do ${cfg.trofeus_ate + 1}º ao ${cfg.medalhas_ate}º` : ""}
          {cfg.trofeu_overall ? " · 1 troféu por Overall" : ""}
        </div>
      )}

      {t && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Kpi label="Troféus" value={t.trofeus} icon={Trophy} testId="premiacao-kpi-trofeus" />
            <Kpi label="Medalhas" value={t.medalhas} icon={Medal} testId="premiacao-kpi-medalhas" />
            <Kpi label="Troféus Overall" value={t.trofeus_overall} icon={Trophy} testId="premiacao-kpi-overall" />
            <Kpi label="Categorias c/ atletas" value={t.categorias_com_atletas} testId="premiacao-kpi-categorias" />
            <Kpi label="Atletas" value={t.atletas} icon={Users} testId="premiacao-kpi-atletas" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card lg:col-span-1 self-start">
              <div className="p-4 border-b border-white/5 overline">Resumo para a mesa</div>
              <table className="table-dense" data-testid="premiacao-resumo">
                <tbody>
                  {Object.entries(t.trofeus_por_colocacao).map(([p, n]) => (
                    <tr key={`t${p}`} data-testid={`premiacao-trofeu-${p}`}>
                      <td className="flex items-center gap-2"><Trophy size={13} className="text-[#C9A84C]" /> Troféu de {posLabel(p)} lugar</td>
                      <td className="text-right font-display font-bold text-lg tabular-nums">{n}</td>
                    </tr>
                  ))}
                  {Object.entries(t.medalhas_por_colocacao).map(([p, n]) => (
                    <tr key={`m${p}`} data-testid={`premiacao-medalha-${p}`}>
                      <td className="flex items-center gap-2"><Medal size={13} className="text-[var(--text-secondary)]" /> Medalha de {posLabel(p)} lugar</td>
                      <td className="text-right font-display font-bold text-lg tabular-nums">{n}</td>
                    </tr>
                  ))}
                  {t.trofeus_overall > 0 && (
                    <tr data-testid="premiacao-trofeu-overall-row">
                      <td className="flex items-center gap-2"><Trophy size={13} className="text-[#C9A84C]" /> Troféu Overall</td>
                      <td className="text-right font-display font-bold text-lg tabular-nums">{t.trofeus_overall}</td>
                    </tr>
                  )}
                  <tr className="border-t border-[#C9A84C]/40">
                    <td className="font-bold text-[#C9A84C]">TOTAL DE TROFÉUS</td>
                    <td className="text-right font-display font-black text-xl text-[#C9A84C] tabular-nums" data-testid="premiacao-total-trofeus">{t.trofeus}</td>
                  </tr>
                  <tr>
                    <td className="font-bold">TOTAL DE MEDALHAS</td>
                    <td className="text-right font-display font-black text-xl tabular-nums" data-testid="premiacao-total-medalhas">{t.medalhas}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="card lg:col-span-2 overflow-x-auto">
              <table className="table-dense" data-testid="premiacao-categorias">
                <thead>
                  <tr>
                    <th className="w-12">Nº</th>
                    <th>Categoria</th>
                    <th className="text-center">Atletas</th>
                    <th className="text-center">Troféus</th>
                    <th className="text-center">Medalhas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.category_id} data-testid={`premiacao-row-${r.category_id}`} className={r.atletas === 0 ? "opacity-40" : ""}>
                      <td className="tabular-nums text-[var(--text-secondary)]">{r.ordem ?? "—"}</td>
                      <td className="font-medium">{r.nome}</td>
                      <td className="text-center tabular-nums">{r.atletas}</td>
                      <td className="text-center">
                        {r.trofeus ? (
                          <span className="inline-flex items-center gap-1 text-[#C9A84C] font-bold tabular-nums"><Trophy size={12} /> {r.trofeus}</span>
                        ) : "—"}
                      </td>
                      <td className="text-center tabular-nums">{r.medalhas || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
